import { SendOutlined } from '@ant-design/icons';
import { Button, Checkbox, Typography } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { useEffect, useState } from 'react';
import useCreateNote from '../../hooks/useCreateNote';
import { useLoggedInUser } from '../../hooks/useLoggedInUser';
import rfsTheme, { relatedNoteBackgroundColour } from '../../theme';
import PageLoadingSpinner from '../PageLoadingSpinner';
import { Optional } from '../Shared';
import Note from './Note';
import type { RelatedNoteWithCreatedAtAndUpdatedAtAsNumber } from './types';
import { noteValidator } from './util';
import { useNotify } from '../../hooks/useNotify';
import { onApolloError } from '../../lib/graphql/helpers';
import type { NoteContent } from '../../hooks/types';

const { Title } = Typography;

type NoteProps = {
  readonly backgroundColor: string;
  readonly readonly: boolean;
};

const addNoteProps =
  (backgroundColor: string, readonly: boolean) =>
  (
    note: RelatedNoteWithCreatedAtAndUpdatedAtAsNumber
  ): {
    readonly note: RelatedNoteWithCreatedAtAndUpdatedAtAsNumber;
  } & NoteProps => ({
    note,
    backgroundColor,
    readonly,
  });

export const Notes: React.FC<{
  /**
   * Applies custom formatting to the note content before it is saved.
   * Use this to set the note structure in the database.
   *
   * @param note - The note as typed by the used.
   * @returns The note as it should be displayed.
   */
  readonly formatOnSave: (note: string) => NoteContent;
  /**
   * The notes to display.
   * These notes will always be displayed
   */
  readonly notes: readonly RelatedNoteWithCreatedAtAndUpdatedAtAsNumber[];
  /**
   * Related notes to display.
   * These notes will only be displayed if the user checks the related notes checkbox.
   */
  readonly relatedNotes: readonly RelatedNoteWithCreatedAtAndUpdatedAtAsNumber[];
  /**
   * Notes can be associated with other objects. This callback is called
   * when the user clicks on the button on the note that is associated with another object,
   * which promises (in its tooltip text) to open that associated object.
   *
   * @param objectId - The ID of the object that the note is associated with
   * @returns void
   */
  readonly onObjectRequested?: (objectId: string) => void;
  /**
   * The notes passed into this component can be for different objects.
   * This is the ID of the object that the notes are primarily associated
   * with; not its root or otherwise related notes.
   * This is the id that will be assigned when creating new notes.
   */
  readonly primaryObjectId: string;
  /**
   * Whether or not the notes are read only
   */
  readonly readonly: boolean;
  /**
   * Determines how the inner card content will be rendered for these notes.
   * This is the content of the note, not the header.
   *
   * @param noteContent - The content of the note to render
   * @returns The inner note content
   */
  readonly render: (
    note: RelatedNoteWithCreatedAtAndUpdatedAtAsNumber
  ) => JSX.Element;
}> = ({
  notes: providedNotes,
  relatedNotes,
  primaryObjectId,
  readonly,
  formatOnSave,
  render,
}) => {
  const { notify } = useNotify();
  const [notes, setNotes] =
    useState<readonly RelatedNoteWithCreatedAtAndUpdatedAtAsNumber[]>(
      providedNotes
    );

  const { loggedInUser } = useLoggedInUser();

  const [mutationCreateNote] = useCreateNote(onApolloError(notify.error));

  const [newNote, setNewNote] = useState<string | undefined>(undefined);

  const [showRelatedNotes, setShowRelatedNotes] = useState(false);

  useEffect(() => {
    setNotes(providedNotes);
  }, [providedNotes]);

  if (loggedInUser === undefined) {
    return <PageLoadingSpinner />;
  }

  const onAddNote = () => {
    const validatedNote = noteValidator(newNote);
    if (validatedNote.isValid) {
      void mutationCreateNote({
        variables: {
          content: formatOnSave(validatedNote.note),
          objectId: primaryObjectId,
        },
        refetchQueries: 'active',
      });
      setNewNote(undefined);
    }
  };

  // Determine the background colour and whether the note
  // should be readonly, then sort the notes by creation date.
  const sortedNotes: readonly ({
    readonly note: RelatedNoteWithCreatedAtAndUpdatedAtAsNumber;
  } & NoteProps)[] = [
    ...[
      notes.map(addNoteProps(rfsTheme.bodyBackgroundColour, readonly)),
      showRelatedNotes
        ? relatedNotes.map(
            // All related notes are always readonly.
            addNoteProps(relatedNoteBackgroundColour, true)
          )
        : [],
    ].flat(),
  ].sort((a, b) => b.note.createdAt - a.note.createdAt);

  return (
    <div
      style={{ maxHeight: '600px', display: 'flex', flexDirection: 'column' }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}
      >
        <Title level={5} style={{ margin: 0 }}>
          Notes
        </Title>
        <div style={{ display: 'flex' }}>
          <div
            style={{
              backgroundColor: relatedNoteBackgroundColour,
              borderRadius: '5px',
              maxWidth: 'fit-content',
              paddingLeft: 5,
              paddingRight: 5,
              marginLeft: 5,
              marginRight: 5,
            }}
          >
            Related Notes
          </div>
          <Checkbox
            checked={showRelatedNotes}
            onChange={() => setShowRelatedNotes(!showRelatedNotes)}
          ></Checkbox>
        </div>
      </div>
      <div style={{ overflowY: 'auto' }}>
        {sortedNotes.map((note, i) => (
          <Note
            {...note}
            key={i}
            loggedInUserId={loggedInUser.id}
            formatOnSave={formatOnSave}
            render={render}
          />
        ))}
        <Optional
          show={
            // We are showing related notes
            showRelatedNotes &&
            // There are no related notes
            relatedNotes.length === 0
          }
        >
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div
              style={{
                backgroundColor: relatedNoteBackgroundColour,
                borderRadius: '5px',
                maxWidth: 'fit-content',
                paddingLeft: 5,
                paddingRight: 5,
                marginBottom: 5,
              }}
            >
              No related notes found
            </div>
          </div>
        </Optional>
      </div>
      <Optional show={!readonly}>
        <div
          style={{ display: 'flex', marginBottom: '10px', marginTop: '5px' }}
        >
          <TextArea
            placeholder="Add a note..."
            value={newNote}
            onChange={(e) => setNewNote(e.currentTarget.value)}
            allowClear
            maxLength={1000}
            showCount
            style={{ flexGrow: 1 }}
          />
          {/* Provide padding under button to make space for character counter */}
          <div style={{ paddingBottom: 22 }}>
            <Button
              type="primary"
              onClick={onAddNote}
              disabled={!noteValidator(newNote).isValid}
              style={{ height: '100%' }}
            >
              <SendOutlined />
            </Button>
          </div>
        </div>
      </Optional>
    </div>
  );
};
